import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import CustomInput from "../../../../Components/UI/Inputs/CustomInput";
import { updateUser } from "../../../../services/AdminServices/Users/users.services";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import Countries from "../../../../data/countries";
import { IoChevronDown } from "react-icons/io5";
import { convertTime } from "../../../../helpers/helpers";
import { useTranslation } from "react-i18next";

const ShipperDetails = ({ shipperData, updateShipper }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [countryDropdown, setCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!shipperData) return;
    setForm({
      id: shipperData?.id ?? "",
      username: shipperData?.username ?? "",
      firstname: shipperData?.firstname ?? "",
      lastname: shipperData?.lastname ?? "",
      business_name: shipperData?.business_name ?? "",
      industry_type: shipperData?.industry_type ?? "",
      email: shipperData?.email ?? "",
      phone_number: shipperData?.phone_number ?? "",
      address: shipperData?.address ?? "",
      country: shipperData?.country ?? shipperData?.Country ?? "",
      province: shipperData?.province ?? "",
      city: shipperData?.city ?? "",
      zip: shipperData?.zip ?? "",
      about_us: shipperData?.about_us ?? "",
      shipment_per_month: shipperData?.shipment_per_month ?? "",
      revenue: shipperData?.revenue ?? "",
      role: shipperData?.role ?? "",
      is_questions_approved: shipperData?.is_questions_approved ?? "",
      balance: shipperData?.balance ?? "",
      website: shipperData?.website ?? "",
      account_status: shipperData?.account_status ?? "",
      created_at: shipperData?.created_at ?? "",
      updated_at: shipperData?.updated_at ?? "",
    });

    const currentCountry = Countries?.find(
      (d) => d?.code === (shipperData?.country ?? shipperData?.Country ?? "")
    );
    setCountrySearch(currentCountry?.name || "");
  }, [shipperData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdown && !event.target.closest(".country-dropdown")) {
        setCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [countryDropdown]);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCountrySelect = (country) => {
    setForm((prev) => ({ ...prev, country: country.code }));
    setCountrySearch(country.name);
    setCountryDropdown(false);
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setCountrySearch(value);
    setCountryDropdown(true);

    if (value === "") {
      setForm((prev) => ({ ...prev, country: "" }));
    }
  };

  const filteredCountries = Countries.filter((country) =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!form?.id) return;
    setIsSaving(true);

    const apiData = {
      firstname: form.firstname,
      lastname: form.lastname,
      business_name: form.business_name,
      phone_number: form.phone_number,
      address: form.address,
      country: form.country,
      province: form.province,
      city: form.city,
      zip: form.zip,
      industry_type: form.industry_type,
      revenue: form.revenue,
      shipment_per_month: form.shipment_per_month,
      about_us: form.about_us,
      website: form.website,
    };

    try {
      const response = await updateUser(apiData, form.id);
      if (response?.error || !response?.data?.success) {
        dispatch(
          showSnackBar({ text: t("controls.somethingWentWrong"), severity: "error" })
        );
        return;
      }

      const updated = response?.data?.result;
      if (updated) {
        setForm((prev) => ({ ...prev, ...updated }));

        // ✅ Update parent user state
        updateShipper(form.id, apiData, null);
      }
      dispatch(
        showSnackBar({
          text: t("controls.userUpdatedSuccessfully"),
          severity: "success",
        })
      );
      setIsEditing(false);
    } catch (error) {
      dispatch(
        showSnackBar({ text: t("controls.somethingWentWrong"), severity: "error" })
      );
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-6">
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-ui-border">
          <h3 className="text-lg font-semibold text-ui-text">
            {t("controls.basicInformation")}
          </h3>
          <BoltButton
            label={isEditing ? t("common:actions.save") : t("common:actions.edit")}
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
            variant="primary"
            size="md"
            disabled={isSaving}
            loading={isSaving}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("controls.username")}
              </label>
              <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                {form?.username || "-"}
              </div>
            </div>

            {isEditing ? (
              <CustomInput
                label={t("controls.firstName")}
                value={form?.firstname}
                onChange={handleChange("firstname")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.firstName")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.firstname || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.businessName")}
                value={form?.business_name}
                onChange={handleChange("business_name")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.businessName")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.business_name || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.address")}
                value={form?.address}
                onChange={handleChange("address")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.address")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.address || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.city")}
                value={form?.city}
                onChange={handleChange("city")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.city")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.city || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.zipCode")}
                value={form?.zip}
                onChange={handleChange("zip")}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.zipCode")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.zip || "-"}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("controls.email")}
              </label>
              <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                {form?.email || "-"}
              </div>
            </div>

            {isEditing ? (
              <CustomInput
                label={t("controls.lastName")}
                value={form?.lastname}
                onChange={handleChange("lastname")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.lastName")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.lastname || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.phoneNumber")}
                value={form?.phone_number}
                onChange={handleChange("phone_number")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.phoneNumber")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.phone_number || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <div className="relative country-dropdown">
                <CustomInput
                  label={t("common:fields.country")}
                  value={countrySearch}
                  onChange={handleCountryChange}
                  onFocus={() => setCountryDropdown(true)}
                  labelClassName="text-xs mb-1"
                  inputClassName="h-9 text-sm px-3 pr-8"
                  placeholder={t("controls.selectCountry")}
                />
                <IoChevronDown className="absolute right-3 top-8 h-4 w-4 text-ui-text-subtle pointer-events-none" />
                {countryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-border rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <div
                          key={country.code}
                          className="px-3 py-2 text-sm text-ui-text hover:bg-ui-surface-sunken cursor-pointer"
                          onClick={() => handleCountrySelect(country)}
                        >
                          {country.name}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-ui-text-muted">
                        {t("controls.noCountriesFound")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("common:fields.country")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {Countries?.find((d) => d?.code === form?.country)?.name ||
                    form?.country ||
                    "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.province")}
                value={form?.province}
                onChange={handleChange("province")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.province")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.province || "-"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-6">
        <h3 className="text-lg font-semibold text-ui-text mb-4 pb-2 border-b border-ui-border">
          {t("controls.additionalInformation")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {isEditing ? (
              <CustomInput
                label={t("controls.industryType")}
                value={form?.industry_type}
                onChange={handleChange("industry_type")}
                readOnly={!isEditing}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.industryType")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.industry_type || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.estShipmentPerMonth")}
                value={form?.shipment_per_month}
                onChange={handleChange("shipment_per_month")}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.estShipmentPerMonth")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.shipment_per_month || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.grossRevenuePerYear")}
                value={form?.revenue}
                onChange={handleChange("revenue")}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.grossRevenuePerYear")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.revenue ? `$${form?.revenue}` : "-"}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ui-text-muted mb-1">
                {t("controls.marketplaces")}
              </label>
              <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                {shipperData?.market?.length
                  ? shipperData["market"]?.map((markets, index) => (
                      <span
                        key={index}
                        className="inline-block bg-brand-light text-brand px-2 py-1 rounded-md text-xs mr-1 mb-1"
                      >
                        {markets["market"]}
                      </span>
                    ))
                  : "-"}
              </div>
            </div>

            {isEditing ? (
              <CustomInput
                label={t("controls.hearAboutUs")}
                value={form?.about_us}
                onChange={handleChange("about_us")}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.hearAboutUs")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.about_us || "-"}
                </div>
              </div>
            )}

            {isEditing ? (
              <CustomInput
                label={t("controls.website")}
                value={form?.website}
                onChange={handleChange("website")}
                readOnly={false}
                labelClassName="text-xs mb-1"
                inputClassName="h-9 text-sm px-3"
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-ui-text-muted mb-1">
                  {t("controls.website")}
                </label>
                <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
                  {form?.website ? (
                    <a
                      href={
                        form?.website?.startsWith("http")
                          ? form?.website
                          : `http://${form?.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:text-brand-hover underline"
                    >
                      {form?.website}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Details */}
      <div className="bg-ui-surface rounded-lg border border-ui-border p-6">
        <h3 className="text-lg font-semibold text-ui-text mb-4 pb-2 border-b border-ui-border">
          {t("controls.accountDetails")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">
              {t("controls.idCaps")}
            </label>
            <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
              {form?.id || "-"}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">
              {t("controls.role")}
            </label>
            <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
              {form?.role || "-"}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">
              {t("controls.balance")}
            </label>
            <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
              {form?.balance || "-"}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">
              {t("controls.accountActivatedAt")}
            </label>
            <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
              {convertTime(form?.account_status)}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">
              {t("controls.registeredAt")}
            </label>
            <div className="text-sm text-ui-text bg-ui-surface-sunken px-3 py-2 rounded-md">
              {convertTime(form?.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Companies Status removed from here; shown under Carriers tab */}
    </div>
  );
};

export default ShipperDetails;
